import React, { useCallback, useEffect, useState } from 'react';
import {
  performanceMetricsService,
  TopQuery,
  UrlDetail,
} from '../../../../services/performanceMetricsService';
import { Sparkline } from './Sparkline';
import { NeighborhoodGraph } from './NeighborhoodGraph';
import { ArticlePeriodTrends } from './ArticlePeriodTrends';

type Props = {
  url: string;
  onClose: () => void;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = (n: number | null | undefined): string => {
  if (n == null) return '\u2014';
  if (n >= 10000) return `${(n / 1000).toFixed(0)}k`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

const fmtPct = (n: number | null | undefined): string => {
  if (n == null) return '\u2014';
  return `${(n * 100).toFixed(2)}%`;
};

const fmtPos = (n: number | null | undefined): string => {
  if (n == null) return '\u2014';
  return n.toFixed(1);
};

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '\u2014';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '\u2014';
  }
};

const decodeHtmlEntities = (str: string): string =>
  str
    .replace(/&#038;/g, '&')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#8217;/g, '\u2019')
    .replace(/&#8216;/g, '\u2018')
    .replace(/&#8220;/g, '\u201c')
    .replace(/&#8221;/g, '\u201d')
    .replace(/&#8211;/g, '\u2013')
    .replace(/&#8212;/g, '\u2014')
    .replace(/&hellip;/g, '\u2026')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');

// ---------------------------------------------------------------------------
// ArticleDetailPanel
// ---------------------------------------------------------------------------

export const ArticleDetailPanel: React.FC<Props> = ({ url, onClose }) => {
  const [data, setData] = useState<UrlDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await performanceMetricsService.getOne(url, 28);
      setData(d);
    } catch (e: any) {
      setError(e?.message || 'Failed to load article detail');
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    load();
  }, [load]);

  // Escape key to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const inv = data?.inventory;
  const idx = data?.indexation;
  const title = decodeHtmlEntities(inv?.title || data?.url || url);

  // Sparklines from series
  const clicksSeries = data?.series?.map((d) => d.clicks) || [];
  const impSeries = data?.series?.map((d) => d.impressions) || [];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />

      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-[520px] max-w-full bg-white z-50 shadow-2xl border-l border-gray-200 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-start justify-between gap-3 z-10">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 leading-tight">{title}</h2>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 break-all mt-1 block"
            >
              {url}
            </a>
            {inv && (
              <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                {inv.category && <span>{inv.category}</span>}
                {inv.pillar && inv.from_pipeline && <><span className="text-gray-300">|</span><span>{inv.pillar}</span></>}
                {inv.published_at && <><span className="text-gray-300">|</span><span>{fmtDate(inv.published_at)}</span></>}
                {inv.word_count && <><span className="text-gray-300">|</span><span>{inv.word_count.toLocaleString()} words</span></>}
                {inv.from_pipeline && (
                  <span className="px-1 rounded bg-blue-50 text-blue-600 text-[9px] font-medium uppercase">Talaera</span>
                )}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 -mr-1"
            title="Close (Escape)"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-500" />
          </div>
        )}

        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{error}</div>
        )}

        {data && !loading && (
          <div className="px-6 py-5 space-y-6">
            {/* Data freshness */}
            <div className="flex items-center gap-3 text-[10px] text-gray-400">
              {data.last_date && <span>GSC data through {data.last_date}</span>}
              {data.ga4 && data.ga4.data_points > 0 && data.ga4.series.length > 0 && (
                <span>GA4 data through {data.ga4.series[data.ga4.series.length - 1].date}</span>
              )}
              {!data.last_date && (!data.ga4 || data.ga4.data_points === 0) && (
                <span>No performance data available for this article</span>
              )}
            </div>

            {/* At-a-glance stats (GSC) */}
            <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Search Performance (last {data.window_days} days)</div>
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Total Clicks" value={fmt(data.clicks)} sub={`${(data.clicks / Math.max(data.data_points, 1)).toFixed(1)}/day avg`} tooltip="Source: Google Search Console. Total clicks from all search appearances (organic + AI Overviews)." />
              <StatCard label="Total Impressions" value={fmt(data.impressions)} sub={`${fmt(Math.round(data.impressions / Math.max(data.data_points, 1)))}/day avg`} tooltip="Source: Google Search Console. Total impressions from all search appearances. Includes both organic results and AI Overview citations." />
              <StatCard label="Avg Position" value={fmtPos(data.avg_position)} color={posColor(data.avg_position)} sub={`Across ${data.data_points} days`} tooltip="Source: Google Search Console. Average ranking position across all queries. 1-3 = top of page 1 (best), 4-10 = page 1, 11-20 = page 2." />
              <StatCard label="Overall CTR" value={fmtPct(data.ctr)} sub={data.organic_ctr != null ? `Organic CTR: ${fmtPct(data.organic_ctr)}` : ''} tooltip="Source: Google Search Console. Overall click-through rate including AI Overview impressions. If this is much lower than Organic CTR, AI Overviews are diluting the number." />
            </div>
            {/* AI Overview breakdown */}
            {(data.ai_overview_impressions || 0) > 0 && (
              <>
              <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mt-2">AI Overview Breakdown (last {data.window_days} days)</div>
              <div className="grid grid-cols-3 gap-3">
                <StatCard label="AI Overview Impressions" value={fmt(data.ai_overview_impressions || 0)} sub={`${((data.ai_overview_impressions || 0) / Math.max(data.impressions, 1) * 100).toFixed(1)}% of all impressions`} tooltip="Times this article was cited in a Google AI Overview. These impressions have very low CTR because users read the AI-generated answer without clicking through to the source." />
                <StatCard label="AI Overview Clicks" value={fmt(data.ai_overview_clicks || 0)} sub={data.ai_overview_impressions ? `${((data.ai_overview_clicks || 0) / data.ai_overview_impressions * 100).toFixed(2)}% CTR` : ''} tooltip="Clicks from AI Overview citations. Typically much lower than organic CTR because the AI answer satisfies the user's intent." />
                <StatCard label="Organic CTR" value={data.organic_ctr != null ? fmtPct(data.organic_ctr) : '\u2014'} sub={`${fmt(data.organic_clicks || 0)} clicks / ${fmt(data.organic_impressions || 0)} impressions`} tooltip="Click-through rate excluding AI Overview impressions. This is your true organic search performance — how compelling your title and description are when shown as a regular result." />
              </div>
              </>
            )}

            {/* GA4 stats */}
            {data.ga4 && data.ga4.data_points > 0 ? (
              <>
              {/* Volume */}
              <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mt-2">Volume (last {data.window_days} days, {data.ga4.data_points} days with data)</div>
              <div className="grid grid-cols-3 gap-3">
                <StatCard label="Pageviews" value={fmt(data.ga4.pageviews)} sub={`${(data.ga4.pageviews / Math.max(data.ga4.data_points, 1)).toFixed(1)}/day avg`} tooltip="Source: Google Analytics (GA4). Total page loads including repeat views by the same user. Higher than unique users if people revisit or refresh the page." />
                <StatCard label="Unique Users" value={fmt(data.ga4.total_users || 0)} sub={`${((data.ga4.total_users || 0) / Math.max(data.ga4.data_points, 1)).toFixed(1)}/day avg`} tooltip="Source: Google Analytics (GA4). Distinct individuals who visited this page. More accurate than sessions for measuring actual audience reach, since one person can have multiple sessions." />
                <StatCard label="Sessions" value={fmt(data.ga4.sessions)} sub={`${fmt(Math.round(data.ga4.sessions / Math.max(data.ga4.data_points, 1)))}/day avg`} tooltip="Source: Google Analytics (GA4). Total visits from all traffic sources (organic, direct, social, email, referral). Unlike GSC clicks which only count search traffic, this captures the full picture." />
              </div>
              {/* Attention & Retention */}
              <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mt-2">Engagement (last {data.window_days} days)</div>
              <div className="grid grid-cols-3 gap-3">
                <StatCard label="Avg Read Time" value={data.ga4.avg_engagement_time != null ? `${Math.floor(data.ga4.avg_engagement_time / 60)}m ${Math.round(data.ga4.avg_engagement_time % 60)}s` : '\u2014'} sub="Active time per user" tooltip="Source: Google Analytics (GA4). Calculated: total engagement duration / unique users. Only counts time when the tab is active and in focus — not idle time. For a 2000-word article, 3-5 minutes suggests they actually read it." />
                <StatCard label="Scroll Depth (90%)" value={data.ga4.scroll_rate != null ? `${(data.ga4.scroll_rate * 100).toFixed(1)}%` : '\u2014'} sub={`${fmt(data.ga4.scrolled_users || 0)} users reached end`} tooltip="Source: Google Analytics (GA4). Calculated: users who scrolled to 90% of the page / total users. Tells you if readers reach your CTA and conclusion. Below 30% on a long article suggests content loses readers midway." />
                <StatCard label="Engagement Rate" value={data.ga4.engagement_rate != null ? `${(data.ga4.engagement_rate * 100).toFixed(1)}%` : '\u2014'} sub={data.ga4.bounce_rate != null ? `${(data.ga4.bounce_rate * 100).toFixed(1)}% bounce` : ''} tooltip="Source: Google Analytics (GA4). Calculated: 1 - bounce rate. A session is 'engaged' if the user stayed over 10 seconds, triggered a key event, or viewed 2+ pages. For blog content, high engagement rate means people are actually reading, not just landing and leaving." />
              </div>
              {/* Conversion & Entry */}
              <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mt-2">Outcomes (last {data.window_days} days)</div>
              <div className="grid grid-cols-3 gap-3">
                <StatCard label="Conversions" value={String(data.ga4.conversions)} sub={data.ga4.conversions > 0 ? `${(data.ga4.conversions / Math.max(data.ga4.sessions, 1) * 100).toFixed(2)}% conv. rate` : 'No key events in GA4'} tooltip="Source: Google Analytics (GA4). Count of key events (goals) triggered on this page: form submissions, demo requests, sign-ups, etc. The conversion rate (conversions / sessions) tells you which articles actually drive business outcomes, not just traffic." />
                <StatCard label="Entry Point Sessions" value={fmt(data.ga4.landing_sessions || 0)} sub={data.ga4.sessions > 0 ? `${((data.ga4.landing_sessions || 0) / data.ga4.sessions * 100).toFixed(1)}% of sessions started here` : ''} tooltip="Source: Google Analytics (GA4). Sessions where this page was the first page visited. A high percentage means this article is a primary entry point to your site — visitors are discovering you through this content, not navigating to it internally." />
              </div>
              {/* Traffic Sources — full width */}
              <div className="border border-gray-200 rounded-lg px-4 py-3">
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 cursor-help border-b border-dotted border-gray-300 inline-block" title="Source: Google Analytics (GA4). Breakdown of traffic sources for this article. AI referral traffic (ChatGPT, Perplexity, etc.) is included in the Referral category and also reported separately below.">Traffic Sources</div>
                {data.ga4.top_channels && data.ga4.top_channels.length > 0 ? (
                  <div className="space-y-1.5">
                    {data.ga4.top_channels.map((ch) => (
                      <div key={ch.channel} className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 w-28 shrink-0 truncate text-right">{ch.channel}</span>
                        <div className="flex-1 bg-gray-50 rounded-full h-3 overflow-hidden">
                          <div className="h-full rounded-full bg-purple-500" style={{ width: `${(ch.sessions / (data.ga4!.top_channels[0]?.sessions || 1)) * 100}%` }} />
                        </div>
                        <span className="text-[10px] font-mono text-gray-500 w-10 text-right">{ch.sessions}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-300 mt-1">{'\u2014'}</div>
                )}
              </div>
              {/* AI Traffic */}
              {(data.ga4.ai_sessions > 0 || data.ga4.ai_sources) && (
                <>
                <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mt-2">AI Referral Traffic (last {data.window_days} days)</div>
                <div className="grid grid-cols-3 gap-3">
                  <StatCard label="AI Sessions" value={String(data.ga4.ai_sessions)} sub={data.ga4.sessions > 0 ? `${(data.ga4.ai_sessions / data.ga4.sessions * 100).toFixed(1)}% of all sessions` : ''} tooltip="Sessions referred from AI tools like ChatGPT, Perplexity, Gemini, Claude, and Copilot. Shows how much of this article's traffic comes from AI-generated recommendations." />
                  {data.ga4.ai_sources && Object.entries(data.ga4.ai_sources).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([src, cnt]) => (
                    <StatCard key={src} label={src} value={String(cnt)} tooltip={`Sessions from ${src}`} />
                  ))}
                </div>
                </>
              )}
              {data.ga4.ai_sessions === 0 && !data.ga4.ai_sources && (
                <div className="text-[10px] text-gray-300 mt-2">No AI referral traffic detected for this article.</div>
              )}
              </>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-xs text-gray-400">
                No Google Analytics data for this article in the last {data.window_days} days.
                {data.data_points > 0 && ' Search Console data is available above.'}
              </div>
            )}

            {/* Weekly + monthly trends (comparison tables + period bar charts) */}
            <ArticlePeriodTrends data={data} />

            {/* Trend charts */}
            <Section title="Daily clicks (28d)">
              <Sparkline values={clicksSeries} width={460} height={48} />
            </Section>

            <Section title="Daily impressions (28d)">
              <Sparkline values={impSeries} width={460} height={48} strokeColor="#8b5cf6" fillColor="rgba(139, 92, 246, 0.12)" />
            </Section>

            {/* Top queries driving clicks */}
            <Section title={`Top queries by clicks${data.top_queries_date ? ` (${data.top_queries_date})` : ''}`}>
              <QueriesTable queries={data.top_queries_by_clicks} />
            </Section>

            {/* Top queries driving impressions */}
            <Section title="Top queries by impressions">
              <QueriesTable queries={data.top_queries_by_impressions} highlight="impressions" />
            </Section>

            {/* Indexation */}
            {idx && (
              <Section title="Indexation status">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div className="text-gray-500">Indexed</div>
                  <div className={idx.is_indexed ? 'text-green-600 font-medium' : 'text-red-500 font-medium'}>
                    {idx.is_indexed ? 'Yes' : 'No'}
                  </div>
                  <div className="text-gray-500">Verdict</div>
                  <div className="text-gray-900">{idx.verdict || '\u2014'}</div>
                  <div className="text-gray-500">Coverage</div>
                  <div className="text-gray-900">{idx.coverage_state || '\u2014'}</div>
                  <div className="text-gray-500">Last crawled</div>
                  <div className="text-gray-900">{fmtDate(idx.last_crawled)}</div>
                  <div className="text-gray-500">Google canonical</div>
                  <div className="text-gray-900 break-all text-xs">{idx.google_canonical || '\u2014'}</div>
                  <div className="text-gray-500">Checked at</div>
                  <div className="text-gray-900">{fmtDate(idx.checked_at)}</div>
                </div>
              </Section>
            )}

            {/* Link Structure — mini force graph, lazy loaded */}
            <NeighborhoodGraph url={url} />
          </div>
        )}
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StatCard: React.FC<{ label: string; value: string; sub?: string; color?: string; tooltip?: string }> = ({ label, value, sub, color, tooltip }) => (
  <div className="border border-gray-200 rounded-lg px-4 py-3" title={tooltip}>
    <div className={`text-xs text-gray-500 uppercase tracking-wider mb-1 ${tooltip ? 'cursor-help border-b border-dotted border-gray-300 inline-block' : ''}`}>{label}</div>
    <div className={`text-2xl font-bold tabular-nums leading-tight ${color || 'text-gray-900'}`}>{value}</div>
    {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{title}</h3>
    {children}
  </div>
);

const QueriesTable: React.FC<{ queries: TopQuery[]; highlight?: 'clicks' | 'impressions' }> = ({ queries, highlight = 'clicks' }) => {
  if (!queries || queries.length === 0) {
    return <div className="text-sm text-gray-400">No query data available</div>;
  }
  return (
    <div className="divide-y divide-gray-100">
      <div className="flex items-center gap-2 py-1 text-[10px] font-medium text-gray-400 uppercase">
        <span className="flex-1">Query</span>
        <span className="w-14 text-right">Clicks</span>
        <span className="w-16 text-right">Imprs</span>
        <span className="w-12 text-right">Pos</span>
        <span className="w-12 text-right">CTR</span>
      </div>
      {queries.map((q, i) => (
        <div key={i} className="flex items-center gap-2 py-1.5 text-sm">
          <span className="flex-1 text-gray-900 truncate" title={q.query}>{q.query}</span>
          <span className={`w-14 text-right tabular-nums ${highlight === 'clicks' ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
            {q.clicks}
          </span>
          <span className={`w-16 text-right tabular-nums ${highlight === 'impressions' ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
            {fmt(q.impressions)}
          </span>
          <span className="w-12 text-right tabular-nums text-gray-500">{fmtPos(q.position)}</span>
          <span className="w-12 text-right tabular-nums text-gray-500">{fmtPct(q.ctr)}</span>
        </div>
      ))}
    </div>
  );
};

// Position color
const posColor = (pos: number | null | undefined): string => {
  if (pos == null) return 'text-gray-400';
  if (pos <= 3) return 'text-green-600';
  if (pos <= 10) return 'text-gray-900';
  if (pos <= 20) return 'text-amber-600';
  return 'text-gray-400';
};


// Expected CTR by position (rough industry averages)
const expectedCtr = (pos: number): string => {
  if (pos <= 1) return '28';
  if (pos <= 2) return '15';
  if (pos <= 3) return '11';
  if (pos <= 4) return '8';
  if (pos <= 5) return '6';
  if (pos <= 10) return '2-5';
  return '<2';
};
